import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { DEFECT_TYPES, DEFECT_STATUS, SEVERITY, KANBAN_COLUMNS, WARRANTY_TYPES, WARRANTY_FAMILIES, WARRANTY_FAMILY_KEYS, formatEUR, formatDate } from "@/lib/dict";
import { Plus, Search, Filter, LayoutGrid, Table as TableIcon, FileText, Trash2, Download, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import FileUploader from "@/components/FileUploader";
import AuthImage from "@/components/AuthImage";
import CommentsPanel from "@/components/CommentsPanel";
import { exportToCsv } from "@/lib/csvExport";
import SavedViews from "@/components/SavedViews";
import { usePickLists } from "@/hooks/usePickLists";

function StatusBadge({ status }) {
  const s = DEFECT_STATUS[status] || { label: status, color: "bg-slate-100 text-slate-700" };
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ${s.color}`}>{s.label}</span>;
}
function SeverityBadge({ severity }) {
  const pl = usePickLists();
  const s = SEVERITY[severity] || SEVERITY.minor;
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${s.color}`}>{pl.labelOf("defect.severity", severity, SEVERITY)}</span>;
}

export default function Defects() {
  const qc = useQueryClient();
  const pl = usePickLists();
  const { user } = useAuth();
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [warrantyTab, setWarrantyTab] = useState(() => {
    const w = searchParams.get("warranty");
    return w && ["GPA", "GBF", "decennial", "DO", "none"].includes(w) ? w : "all";
  });
  const [filters, setFilters] = useState({ status: "all", severity: "all", type: "all", client_id: "all", legal_entity_id: "all", asset_id: "all" });
  const [activeViewName, setActiveViewName] = useState(null);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Sync tab -> URL so the tab is deep-linkable
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (warrantyTab === "all") params.delete("warranty");
    else params.set("warranty", warrantyTab);
    setSearchParams(params, { replace: true });
  }, [warrantyTab, searchParams, setSearchParams]);

  const { data: defects = [] } = useQuery({ queryKey: ["defects"], queryFn: () => api.get("/defects").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });
  const { data: legalEntities = [] } = useQuery({ queryKey: ["legal-entities"], queryFn: () => api.get("/legal-entities").then((r) => r.data) });
  const { data: contractors = [] } = useQuery({ queryKey: ["users", "contractor"], queryFn: () => api.get("/users?role=contractor").then((r) => r.data) });
  // Aggregate attachment counts per defect so the list badges are always in sync with uploads.
  const { data: allDocs = [] } = useQuery({ queryKey: ["docs-all"], queryFn: () => api.get("/documents").then((r) => r.data) });
  const docCounts = useMemo(() => {
    const c = {};
    allDocs.forEach((d) => { if (d.defect_id) c[d.defect_id] = (c[d.defect_id] || 0) + 1; });
    return c;
  }, [allDocs]);

  // Counts per warranty family (based on unfiltered defects) — used to badge the tabs
  const warrantyCounts = useMemo(() => {
    const counts = { all: defects.length, GPA: 0, GBF: 0, decennial: 0, DO: 0, none: 0 };
    defects.forEach((d) => { const k = d.warranty_type || "none"; counts[k] = (counts[k] || 0) + 1; });
    return counts;
  }, [defects]);

  const assetMap = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a])), [assets]);

  // Cascading options for Client → Legal entity → Asset
  const legalEntitiesForClient = useMemo(() => {
    if (filters.client_id === "all") return legalEntities;
    return legalEntities.filter((le) => le.client_id === filters.client_id);
  }, [legalEntities, filters.client_id]);

  const assetsForScope = useMemo(() => {
    return assets.filter((a) => {
      if (filters.client_id !== "all" && a.client_id !== filters.client_id) return false;
      if (filters.legal_entity_id !== "all" && a.legal_entity_id !== filters.legal_entity_id) return false;
      return true;
    });
  }, [assets, filters.client_id, filters.legal_entity_id]);

  const filtered = useMemo(() => defects.filter((d) => {
    if (warrantyTab !== "all") {
      const wt = d.warranty_type || "none";
      if (wt !== warrantyTab) return false;
    }
    if (filters.status !== "all" && d.status !== filters.status) return false;
    if (filters.severity !== "all" && d.severity !== filters.severity) return false;
    if (filters.type !== "all" && d.type !== filters.type) return false;
    // Client / Legal entity / Asset scoping — walk through the asset the defect is attached to
    if (filters.asset_id !== "all") {
      if (d.asset_id !== filters.asset_id) return false;
    } else if (filters.legal_entity_id !== "all" || filters.client_id !== "all") {
      const a = assetMap[d.asset_id];
      if (!a) return false;
      if (filters.legal_entity_id !== "all" && a.legal_entity_id !== filters.legal_entity_id) return false;
      if (filters.client_id !== "all" && a.client_id !== filters.client_id) return false;
    }
    if (search && !`${d.reference} ${d.title}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [defects, filters, search, warrantyTab, assetMap]);

  const updateStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/defects/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["defects"] }); toast.success("Statut mis à jour"); },
  });

  const createDefect = useMutation({
    mutationFn: (data) => api.post("/defects", data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["defects"] }); toast.success("Désordre créé"); setCreateOpen(false); },
  });

  const assignContractor = useMutation({
    mutationFn: ({ id, contractor_id }) => api.patch(`/defects/${id}`, { assigned_contractor_id: contractor_id, status: "assigned" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["defects"] }); toast.success("Entreprise assignée"); },
  });

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Désordres</h1>
          <p className="text-sm text-slate-500 mt-1.5">{filtered.length} désordre(s) — vues tableau et kanban</p>
        </div>
        <div className="flex gap-2">
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-defect-btn" className="text-white" style={{ background: "#023E73" }}><Plus size={16} className="mr-1.5" /> Nouveau désordre</Button>
          </DialogTrigger>
          <DefectCreateDialog assets={assets} contractors={contractors} onSubmit={(d) => createDefect.mutate(d)} />
        </Dialog>
        <Button
          data-testid="export-defects-csv" variant="outline"
          onClick={() => exportToCsv("desordres.csv", filtered, [
            { key: "reference", label: "Référence" },
            { key: "title", label: "Titre" },
            { key: "type", label: "Type", transform: (r) => pl.labelOf("defect.type", r.type, DEFECT_TYPES) },
            { key: "severity", label: "Gravité", transform: (r) => pl.labelOf("defect.severity", r.severity, SEVERITY) },
            { key: "status", label: "Statut", transform: (r) => DEFECT_STATUS[r.status]?.label },
            { key: "asset", label: "Actif", transform: (r) => assetMap[r.asset_id]?.name },
            { key: "warranty_type", label: "Garantie", transform: (r) => WARRANTY_TYPES[r.warranty_type] || "" },
            { key: "date_observed", label: "Observé le" },
            { key: "estimated_cost", label: "Coût estimé €" },
            { key: "actual_cost", label: "Coût réel €" },
          ])}
        ><Download size={14} className="mr-1.5" /> Export CSV</Button>
        <SavedViews
          page="defects"
          currentFilters={{ warrantyTab, status: filters.status, severity: filters.severity, type: filters.type, client_id: filters.client_id, legal_entity_id: filters.legal_entity_id, asset_id: filters.asset_id, search }}
          activeName={activeViewName}
          onApply={(f, name) => {
            setWarrantyTab(f.warrantyTab || "all");
            setFilters({
              status: f.status || "all",
              severity: f.severity || "all",
              type: f.type || "all",
              client_id: f.client_id || "all",
              legal_entity_id: f.legal_entity_id || "all",
              asset_id: f.asset_id || "all",
            });
            setSearch(f.search || "");
            setActiveViewName(name);
          }}
          onClearActive={() => setActiveViewName(null)}
        />
        </div>
      </div>

      {/* Warranty family tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-2" data-testid="warranty-tabs">
        {[{ k: "all", label: "Tous", color: "#023E73" }, ...WARRANTY_FAMILY_KEYS.map((k) => ({ k, label: WARRANTY_FAMILIES[k].label, color: WARRANTY_FAMILIES[k].color }))].map((t) => {
          const active = warrantyTab === t.k;
          const count = warrantyCounts[t.k] || 0;
          return (
            <button
              key={t.k}
              data-testid={`warranty-tab-${t.k}`}
              onClick={() => setWarrantyTab(t.k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition ${active ? "text-white border-transparent shadow-sm" : "text-slate-600 border-slate-200 bg-white hover:bg-slate-50"}`}
              style={active ? { background: t.color } : {}}
            >
              {t.k !== "all" && <span className="w-2 h-2 rounded-full" style={{ background: active ? "white" : t.color }} />}
              {t.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? "bg-white/25" : "bg-slate-100"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-3 border-slate-200">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="defects-search" placeholder="Rechercher (réf., titre...)" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 text-sm" />
          </div>
          <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v, legal_entity_id: "all", asset_id: "all" })}>
            <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.legal_entity_id} onValueChange={(v) => setFilters({ ...filters, legal_entity_id: v, asset_id: "all" })}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-legal-entity"><SelectValue placeholder="Entité juridique" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes entités</SelectItem>
              {legalEntitiesForClient.map((le) => <SelectItem key={le.id} value={le.id}>{le.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.asset_id} onValueChange={(v) => setFilters({ ...filters, asset_id: v })}>
            <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="filter-asset"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous sites</SelectItem>
              {assetsForScope.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {Object.entries(DEFECT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.severity} onValueChange={(v) => setFilters({ ...filters, severity: v })}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="filter-severity"><SelectValue placeholder="Gravité" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes gravités</SelectItem>
              {pl.options("defect.severity", SEVERITY).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              {pl.options("defect.type", DEFECT_TYPES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filters.client_id !== "all" || filters.legal_entity_id !== "all" || filters.asset_id !== "all" || filters.status !== "all" || filters.severity !== "all" || filters.type !== "all" || search) && (
            <Button
              data-testid="reset-filters"
              variant="ghost"
              size="sm"
              className="h-9 text-xs text-slate-600"
              onClick={() => { setFilters({ status: "all", severity: "all", type: "all", client_id: "all", legal_entity_id: "all", asset_id: "all" }); setSearch(""); }}
            >
              Réinitialiser
            </Button>
          )}
          <div className="ml-auto inline-flex rounded-md border border-slate-200 p-0.5">
            <button data-testid="view-table" onClick={() => setView("table")} className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 ${view === "table" ? "bg-slate-900 text-white" : "text-slate-600"}`}><TableIcon size={13} /> Tableau</button>
            <button data-testid="view-kanban" onClick={() => setView("kanban")} className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 ${view === "kanban" ? "bg-slate-900 text-white" : "text-slate-600"}`}><LayoutGrid size={13} /> Kanban</button>
          </div>
        </div>
      </Card>

      {/* Table view */}
      {view === "table" && (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Référence</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Titre</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Actif</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Type</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Garantie</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Gravité</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Statut</th>
                  <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Estimé</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} onClick={() => setSelectedDefect(d)} data-testid={`defect-row-${d.id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{d.reference}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-1.5">
                        <span>{d.title}</span>
                        {docCounts[d.id] > 0 && (
                          <span data-testid={`attach-count-${d.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-500" title={`${docCounts[d.id]} pièce(s) jointe(s)`}>
                            <Paperclip size={10} />{docCounts[d.id]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{assetMap[d.asset_id]?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{pl.labelOf("defect.type", d.type, DEFECT_TYPES)}</td>
                    <td className="px-4 py-3">
                      {(() => { const wk = d.warranty_type || "none"; const w = WARRANTY_FAMILIES[wk]; return (
                        <span data-testid={`warranty-pill-${d.id}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border" style={{ background: `${w.color}15`, color: w.color, borderColor: `${w.color}40` }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: w.color }} />{w.label}
                        </span>
                      ); })()}
                    </td>
                    <td className="px-4 py-3"><SeverityBadge severity={d.severity} /></td>
                    <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{formatEUR(d.estimated_cost)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-10">Aucun désordre.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Kanban view */}
      {view === "kanban" && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {KANBAN_COLUMNS.map((col) => {
            const items = filtered.filter((d) => d.status === col);
            const s = DEFECT_STATUS[col];
            return (
              <div key={col} className="kanban-col flex-1 min-w-[280px]" data-testid={`kanban-col-${col}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.color}`}>{s.label}</span>
                  <span className="text-xs text-slate-500 font-semibold">{items.length}</span>
                </div>
                {items.map((d) => {
                  const wk = d.warranty_type || "none"; const w = WARRANTY_FAMILIES[wk];
                  return (
                    <div key={d.id} onClick={() => setSelectedDefect(d)} data-testid={`kanban-card-${d.id}`} className="kanban-card cursor-pointer" style={{ borderLeft: `4px solid ${w.color}` }}>
                      <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono mb-1">
                        <span>{d.reference}</span><SeverityBadge severity={d.severity} />
                      </div>
                      <div className="text-sm font-semibold text-slate-900 line-clamp-2">{d.title}</div>
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="text-xs text-slate-500">{assetMap[d.asset_id]?.name}</div>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold" style={{ background: `${w.color}15`, color: w.color }}>{w.label}</span>
                      </div>
                      {d.estimated_cost > 0 && <div className="text-xs text-slate-700 font-mono mt-2">{formatEUR(d.estimated_cost)}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Defect detail sheet */}
      <Sheet open={!!selectedDefect} onOpenChange={(o) => !o && setSelectedDefect(null)}>
        <SheetContent className="sm:max-w-[560px] overflow-y-auto">
          {selectedDefect && (
            <DefectDetail
              defect={selectedDefect}
              asset={assetMap[selectedDefect.asset_id]}
              contractors={contractors}
              onUpdateStatus={(s) => updateStatus.mutate({ id: selectedDefect.id, status: s })}
              onAssign={(cid) => assignContractor.mutate({ id: selectedDefect.id, contractor_id: cid })}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DefectCreateDialog({ assets, contractors, onSubmit }) {
  const pl = usePickLists();
  const [form, setForm] = useState({ title: "", description: "", asset_id: "", type: "infiltration", severity: "minor", estimated_cost: 0, warranty_type: "none" });
  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader><DialogTitle>Nouveau désordre</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Titre</Label><Input data-testid="new-defect-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div><Label>Description</Label><Textarea data-testid="new-defect-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Actif</Label>
            <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
              <SelectTrigger data-testid="new-defect-asset"><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="new-defect-type"><SelectValue /></SelectTrigger>
              <SelectContent>{pl.options("defect.type", DEFECT_TYPES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Gravité</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger data-testid="new-defect-severity"><SelectValue /></SelectTrigger>
              <SelectContent>{pl.options("defect.severity", SEVERITY).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Coût estimé €</Label><Input type="number" data-testid="new-defect-cost" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: Number(e.target.value) })} /></div>
        </div>
        <div><Label>Garantie</Label>
          <Select value={form.warranty_type} onValueChange={(v) => setForm({ ...form, warranty_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucune</SelectItem>
              {pl.options("warranty.type", WARRANTY_TYPES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="new-defect-submit" onClick={() => onSubmit({ ...form, warranty_type: form.warranty_type === "none" ? null : form.warranty_type })} className="text-white" style={{ background: "#023E73" }}>Créer</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function DefectDetail({ defect, asset, contractors, onUpdateStatus, onAssign }) {
  const pl = usePickLists();
  const qc = useQueryClient();
  const { data: docs = [] } = useQuery({
    queryKey: ["docs", "defect", defect.id],
    queryFn: () => api.get(`/documents?defect_id=${defect.id}`).then((r) => r.data),
  });
  return (
    <div>
      <SheetHeader>
        <div className="flex items-center justify-between">
          <div className="font-mono text-xs text-slate-500">{defect.reference}</div>
          <SeverityBadge severity={defect.severity} />
        </div>
        <SheetTitle className="font-display text-xl">{defect.title}</SheetTitle>
        <div className="flex gap-2 mt-2"><StatusBadge status={defect.status} /></div>
      </SheetHeader>

      <div className="mt-6 space-y-5 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Description</div>
          <p className="text-slate-700">{defect.description || "—"}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><div className="text-slate-500">Actif</div><div className="font-semibold text-slate-900">{asset?.name}</div></div>
          <div><div className="text-slate-500">Type</div><div className="font-semibold">{pl.labelOf("defect.type", defect.type, DEFECT_TYPES)}</div></div>
          <div><div className="text-slate-500">Bâtiment / Étage</div><div className="font-semibold">{defect.building || "—"} / {defect.floor || "—"}</div></div>
          <div><div className="text-slate-500">Garantie</div><div className="font-semibold">{defect.warranty_type ? pl.labelOf("warranty.type", defect.warranty_type, WARRANTY_TYPES) : "—"}</div></div>
          <div><div className="text-slate-500">Observé le</div><div className="font-semibold">{formatDate(defect.date_observed)}</div></div>
          <div><div className="text-slate-500">Coût estimé</div><div className="font-semibold font-mono">{formatEUR(defect.estimated_cost)}</div></div>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Actions</div>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Changer le statut</Label>
              <Select onValueChange={(v) => onUpdateStatus(v)} defaultValue={defect.status}>
                <SelectTrigger className="h-9" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(DEFECT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Assigner une entreprise</Label>
              <Select onValueChange={(v) => onAssign(v)} defaultValue={defect.assigned_contractor_id || ""}>
                <SelectTrigger className="h-9" data-testid="detail-assign-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name || `${c.first_name} ${c.last_name}`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Documents / Photos */}
        <div className="border-t border-slate-100 pt-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Photos &amp; documents ({docs.length})</div>
          <FileUploader
            defectId={defect.id}
            assetId={defect.asset_id}
            onUploaded={() => qc.invalidateQueries({ queryKey: ["docs", "defect", defect.id] })}
          />
          {docs.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {docs.map((d) => (
                <div key={d.id} data-testid={`doc-${d.id}`} className="relative aspect-square rounded-md border border-slate-200 overflow-hidden bg-slate-50 group">
                  {d.mime_type?.startsWith("image") ? (
                    <AuthImage src={d.url} alt={d.name} className="w-full h-full object-cover" fallback={<div className="p-2 text-[10px] text-slate-400">{d.name}</div>} />
                  ) : (
                    <div className="p-2 flex flex-col items-center justify-center h-full text-slate-500">
                      <FileText size={20} /><div className="text-[10px] mt-1 text-center line-clamp-2">{d.name}</div>
                    </div>
                  )}
                  <button
                    data-testid={`delete-doc-${d.id}`}
                    onClick={async () => { await api.delete(`/documents/${d.id}`); qc.invalidateQueries({ queryKey: ["docs", "defect", defect.id] }); toast.success("Document supprimé"); }}
                    className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition shadow"
                  ><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="border-t border-slate-100 pt-4">
          <CommentsPanel entityType="defect" entityId={defect.id} />
        </div>
      </div>
    </div>
  );
}
